// @flow
import * as React from 'react'
import { debounce } from 'lodash'
import {
  StyledInput,
  FormSmallText,
  Form,
  FormGroup,
  ErrorMessage,
  ErrorMessageContainer
} from '../../shared/formElements'
import { CTAPrimary, colors } from '../../shared'
import styled from 'styled-components'
import { config } from 'react-awesome-styled-grid'

export type PropsType = {
  resetPasswordEmail: string,
  resetPasswordErrorCode?: string,
  resetPasswordErrorText?: string,
  resetPasswordEmailSent: boolean,
  theme: 'sand' | 'white',
  ctaColor?: string
}

export type DispatchPropsType = {
  resetPassword: (email: string) => void
}

export type StateType = {
  email: string,
  errorMessageEmail?: string,
  isEmailValid: boolean,
  isEmailSent: boolean,
  isEmailResent: boolean
}

export class PasswordReset extends React.PureComponent<PropsType & DispatchPropsType, StateType> {
  constructor (props: PropsType & DispatchPropsType): void {
    super(props)
    this.state = {
      email: this.props.resetPasswordEmail || '',
      errorMessageEmail: '',
      isEmailValid: true,
      isEmailSent: false,
      isEmailResent: false
    }
  }

  componentDidUpdate (prevProps: PropsType, prevState: StateType): void {
    const { resetPasswordErrorCode, resetPasswordErrorText, resetPasswordEmailSent } = this.props

    if (resetPasswordEmailSent) {
      this.setState({
        isEmailSent: true
      })
    }

    if (
      resetPasswordErrorText &&
      resetPasswordErrorCode &&
      resetPasswordErrorText !== prevProps.resetPasswordErrorText &&
      resetPasswordErrorCode !== prevProps.resetPasswordErrorCode
    ) {
      this.setAuthError(resetPasswordErrorText, resetPasswordErrorCode)
    }
  }

  setAuthError = (message: string, code: string): void => {
    let emailErrorMessage
    if (code === 'auth/invalid-email') {
      emailErrorMessage = 'Error: Please check the format of your email'
    } else if (code === 'auth/user-not-found') {
      emailErrorMessage = 'Error: There is no account associated with this email, please sign up!'
    } else {
      emailErrorMessage = message
    }

    this.setState({
      errorMessageEmail: emailErrorMessage,
      isEmailValid: false
    })
  }

  submitResetPassword = debounce(
    (e: SyntheticEvent<HTMLInputElement>): void => {
      e.preventDefault()
      if (this.state.email) {
        this.props.resetPassword(this.state.email)
      }
    },
    300,
    { leading: true, trailing: false }
  )

  resendResetPassword = debounce(
    (e: SyntheticEvent<HTMLInputElement>): void => {
      e.preventDefault()
      if (this.state.email) {
        this.props.resetPassword(this.state.email)

        // shows "SENT!" for 2 sec.
        this.setState(
          { isEmailResent: true },
          (): any => setTimeout((): void => { this.setState({ isEmailResent: false }) }, 2000)
        )
      }
    },
    300,
    { leading: true, trailing: false }
  )

  handleEmailChange = (e: any): void => {
    const email = e.target.value.replace(/\s+/g, '')
    this.setState({
      email,
      isEmailValid: true,
      errorMessageEmail: ''
    })
  }

  render (): React.Node {
    const fontColors = this.props.theme === 'sand' ? colors.sand : colors.white
    const emailInvalid = !this.state.isEmailValid && this.state.errorMessageEmail
    const { theme } = this.props
    return (
      <PasswordResetFormContainer className="dummmm">
        {!this.state.isEmailSent && (
          <>
            <BodyText color={fontColors}>Email address associated with your Dipsea account</BodyText>
            <Form onSubmit={this.submitResetPassword}>
              <FormGroup>
                <ErrorMessageContainer>
                  <ErrorMessage showError={emailInvalid}>{this.state.errorMessageEmail}</ErrorMessage>
                </ErrorMessageContainer>
                <StyledInput
                  name="email"
                  type="email"
                  placeholder="Email"
                  className="password-reset-email"
                  value={this.state.email}
                  onChange={this.handleEmailChange}
                  valid={!emailInvalid}
                  invertColor
                  styles={{ width: '100%' }}
                  color={this.props.theme === 'sand' ? colors.sand : colors.white}
                  borderColor={this.props.theme === 'sand' ? colors.sand : colors.white}
                />
              </FormGroup>
              <ButtonContainer>
                <CTAPrimary
                  color={colors.white}
                  type="submit"
                  backgroundColor={this.props.ctaColor || this.props.theme === 'sand' ? colors.orange : colors.blue}
                >
                  SEND
                </CTAPrimary>
              </ButtonContainer>
            </Form>
          </>
        )}
        {this.state.isEmailSent && (
          <EmailSentContainer>
            {this.state.isEmailResent && (
              <SendAgainContainer>
                <SendAgainPopUp>
                  <p>SENT!</p>
                </SendAgainPopUp>
              </SendAgainContainer>
            )}
            <div style={{ paddingTop: '18px', paddingBottom: '18px' }}>
              <BodyText color={theme === 'sand' ? colors.sand : colors.white}>
                We have sent an email to <EmailAddress color={theme === 'sand' ? colors.sand : colors.white}>{`${this.state.email}`}</EmailAddress> to confirm your account. After receiving the email follow the link provided to complete the final step and begin your listening.
              </BodyText>
            </div>
            <div style={{ padding: '2px 0px' }}>
              <hr style={{ border: `solid 1px ${fontColors}` }} size={1}/>
            </div>
            <SendAgainText
              textAlign={'center'}
              color={fontColors}
            >
              {"Didn't receive an email? "}
              <a href="#" onClick={this.resendResetPassword}>
                Resend confirmation email.
              </a>
            </SendAgainText>
          </EmailSentContainer>
        )}
      </PasswordResetFormContainer>
    )
  }
}

const EmailAddress = styled.span`
  color: ${(props: any): string => props.color || colors.sand};
  display: inline;
`
const ButtonContainer = styled.div`
  display:flex;
  justify-content:center;
  margin: 30px 0 0;
  button {
    width: 100%;
  }
`
const BodyText = styled.p`
  color: ${(props: any): any => props.color || colors.sand};
  font-family: 'Good Sans Light';
  font-size: 15px;
  font-style: normal;
  font-weight: ${(props: any): any => props.fontWeight ? props.fontWeight : 'normal'};
  letter-spacing: 0.2px;
  line-height: 18px;
  margin-top: 30px;
  text-align: center;
  width: 100%;
  ${(props: any): string => config(props).media.md`
    text-align: left;
  `}
`
const PasswordResetFormContainer = styled.div`
  align-items: center;
  color: ${colors.white};
  display: flex;
  flex-direction: column;
  width: 100%;
`
const EmailSentContainer = styled.div`
  position: relative;
`
const SendAgainContainer = styled.div`
  bottom:0;
  display:flex;
  justify-content:center;
  left:0;
  position: absolute;
  right:0;
  top:0;
`
const SendAgainPopUp = styled.div`
  align-items: center;
  background-color: ${colors.midnightBlue};
  border-radius: 10px;
  display: flex;
  height: 50px;
  justify-content: center;
  width: 150px;

  p {
    color: ${(props: any): string => props.color || colors.sand};
    font-family: 'Good Sans Regular';
    font-size: 16px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 16px;
  }
`
const SendAgainText = styled(FormSmallText)`
  a {
    color: ${(props: any): string => props.color || colors.sand};
    cursor: pointer;
    font-weight: bold;
    :hover {
      opacity: 0.5;
    }
  }
`
